import { useQuery } from '@tanstack/react-query';
import type { OrgSettings } from '@fint/validation';
import { api } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { todayInTimezone } from '@/lib/dates';

/**
 * The org's settings blob, cached for the session.
 *
 * Read here for two things that affect how numbers are DISPLAYED rather than
 * what they are: `fiscalYearStartMonth`, which decides what "Q1" means, and
 * `tax.regime`, which decides whether a GST screen has anything to say. Both
 * change roughly never, hence the long stale time - refetching them on every
 * page change would be a request per navigation for a value that is fixed.
 */
export function useOrgSettings() {
  const org = useActiveOrg();
  return useQuery({
    queryKey: ['settings', org?.id],
    queryFn: () => api.get<OrgSettings>('/settings'),
    enabled: !!org?.id,
    staleTime: 5 * 60_000,
  });
}

/** April unless the org says otherwise. */
export function useFiscalYearStartMonth(): number {
  return useOrgSettings().data?.fiscalYearStartMonth ?? 4;
}

/** Whether the GST screens have anything to show. */
export function useGstEnabled(): boolean {
  return useOrgSettings().data?.tax?.regime === 'gst';
}

/**
 * The org's base currency.
 *
 * Needed because several API responses - the GST returns above all - report
 * figures without repeating the currency on every row: a return is in one
 * currency by definition. `Money` still demands one, because the currency is
 * what decides the number of decimal places, and a component that guessed two
 * would render a Kuwaiti dinar wrong.
 */
export function useBaseCurrency(): string {
  return useOrgSettings().data?.baseCurrency ?? 'INR';
}

/**
 * Today, in the timezone the books are kept in.
 *
 * ── WHY THE ORG'S TIMEZONE AND NOT THE BROWSER'S ──
 * Because the server derives every status against the ORG's today, and if the
 * two disagree the app contradicts itself. Three date fields used to default to
 * `new Date().toISOString()`, which is today in UTC - wrong for five and a half
 * hours of every day in India, and wrong in a way that shows: a net-0 invoice
 * raised at 01:00 IST was dated yesterday and read as `overdue` the instant it
 * was saved.
 *
 * The browser's own timezone is closer but still not right. An accountant in
 * London working on a Chennai client's books should get the client's date -
 * their invoice belongs to the client's calendar, and at a month boundary it
 * decides which GST return it lands on.
 *
 * Falls back to the browser's zone while settings are still loading, which is
 * the correct answer for the common case and never UTC.
 */
export function useToday(): string {
  return todayInTimezone(useOrgSettings().data?.timezone);
}
